import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { DESCRIPTIONS } from '@/constants/common/message.constants'
import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'

import { DatePicker } from '../DatePicker'

type BaseDPProps = React.ComponentProps<typeof DatePicker>

type NormalizedSingleFieldDateTimePickerProps = Omit<
  BaseDPProps,
  'value' | 'onChange' | 'disabledDate' | 'renderExtraFooter'
> & {
  value?: Date | null
  onChange?: (date: Date | null, dateString: string) => void
  onInvalidText?: (message: string) => void
  onValidText?: () => void

  /**
   * 日付部分をこの日付に固定する（時刻は入力/選択値を使う）
   * - UI上も「日付固定」を明示（固定日以外は選択不可）
   * - fixedDate が変わったら value の日付部分も自動同期
   */
  fixedDate?: Date | null
  /**
   * カレンダーで選べる日の範囲（両端の日を含む、日単位）。fixedDate があればそちらが優先。
   * 経由地の時間欄（#5548）が依頼の発日〜着日に絞るために使う。
   * 端が null / 未指定なら、その側は制限しない。手入力の日付は制限しない
   * （範囲外の値は呼び出し側の保存時検証に任せる）
   */
  selectableDateRange?: { from?: Date | null; to?: Date | null } | null
}

/** 全角→半角 & 空白除去 */
const toHalfWidth = (s: string) => s.normalize('NFKC')

/** Day.js っぽいオブジェクトを Date に変換（antd が dayjs を返す場合に対応） */
type DayjsLike = { toDate: () => Date }
const isDayjsLike = (v: unknown): v is DayjsLike =>
  typeof v === 'object' &&
  v !== null &&
  'toDate' in v &&
  typeof v.toDate === 'function'

const toDate = (v: unknown): Date | null => {
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v
  if (isDayjsLike(v)) {
    const d = v.toDate()
    return d instanceof Date && !isNaN(d.getTime()) ? d : null
  }
  return null
}

/** 厳密な年月日チェック */
function ymdToDateStrict(y: number, m: number, d: number): Date | null {
  const dt = new Date(y, m - 1, d)
  if (
    !isNaN(dt.getTime()) &&
    dt.getFullYear() === y &&
    dt.getMonth() === m - 1 &&
    dt.getDate() === d
  ) {
    return dt
  }
  return null
}

/** 厳密な時刻チェック（0–23, 0–59） */
function isValidHM(hh: number, mi: number): boolean {
  return (
    Number.isInteger(hh) &&
    Number.isInteger(mi) &&
    hh >= 0 &&
    hh < 24 &&
    mi >= 0 &&
    mi < 60
  )
}

/** 入力文字列を YYYY-MM-DD HH:mm に正規化（YYYYMMDDHHmm / 区切り付きも許容） */
function normalizeToYMDHM(raw: string): { date: Date; text: string } | null {
  const s = toHalfWidth(raw).replace(/\s+/g, ' ').trim()

  //YYYYMMDDHHmm
  let m = s.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})$/)
  if (m) {
    const y = +m[1],
      mm = +m[2],
      dd = +m[3],
      hh = +m[4],
      mi = +m[5]
    const ymd = ymdToDateStrict(y, mm, dd)
    if (!ymd || !isValidHM(hh, mi)) return null
    const dt = new Date(y, mm - 1, dd, hh, mi, 0, 0)
    // 逆引き一致でオーバーフロー/DSTずれも検出
    if (
      dt.getFullYear() !== y ||
      dt.getMonth() !== mm - 1 ||
      dt.getDate() !== dd ||
      dt.getHours() !== hh ||
      dt.getMinutes() !== mi
    )
      return null
    return { date: dt, text: dayjs(dt).format('YYYY-MM-DD HH:mm') }
  }

  //YYYY[-/.]MM[-/.]DD[空白 or T]HH:mm
  m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})[ T]+(\d{1,2}):(\d{2})$/)
  if (m) {
    const y = +m[1],
      mm = +m[2],
      dd = +m[3],
      hh = +m[4],
      mi = +m[5]
    const ymd = ymdToDateStrict(y, mm, dd)
    if (!ymd || !isValidHM(hh, mi)) return null
    const dt = new Date(y, mm - 1, dd, hh, mi, 0, 0)
    if (
      dt.getFullYear() !== y ||
      dt.getMonth() !== mm - 1 ||
      dt.getDate() !== dd ||
      dt.getHours() !== hh ||
      dt.getMinutes() !== mi
    )
      return null
    return { date: dt, text: dayjs(dt).format('YYYY-MM-DD HH:mm') }
  }

  return null
}

/** 日付部分だけ fixedDate に差し替える */
function applyFixedDatePart(dt: Date, fixedDate: Date): Date {
  const next = new Date(dt)
  next.setFullYear(
    fixedDate.getFullYear(),
    fixedDate.getMonth(),
    fixedDate.getDate()
  )
  return next
}

/** 同日判定 */
/** 日付の日単位の比較に使う、その日の 0 時のエポックミリ秒（ローカル時刻） */
function startOfDayTime(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function NormalizedSingleFieldDateTimePicker(
  props: NormalizedSingleFieldDateTimePickerProps
) {
  const {
    value,
    onChange,
    onInvalidText,
    onValidText,
    fixedDate,
    selectableDateRange,
    ...rest
  } = props

  const isFixed = !!fixedDate

  // fixedDate 変更時に value の日付部分を自動同期する（時刻は維持）。
  //
  // #5379: この同期は「fixedDate の日が実際に変わったとき」だけ走らせる。
  // value（呼び出し側は value={new Date(field.value)} で毎レンダー新規参照を渡す）や
  // インライン onChange / onValidText を依存配列に入れると、この effect が毎レンダー
  // 実行され、isSameDay ガードだけでは止め切れない。矢羽根（配車）作成モーダルは
  // startAt/endAt の 2 つの fixedDate ピッカーを mode:'onChange' の大型フォーム上に
  // 持ち、着日変更 → endAt の fixedDate 変化 → onChange(setValue) →（非同期 Valibot
  // 検証で value 反映が遅れる間）再レンダーごとに onChange を撃ち続ける、という
  // 再レンダー駆動のバーストに陥ってブラウザがフリーズしていた（配送依頼モーダルは
  // fixedDate ピッカーを持たないため無症状）。
  // fixedDate の「日」をキーにして日が変わった一度だけ同期し、value/onChange/
  // onValidText は ref 経由の最新値を読むことで、再レンダー起因の再発火を構造的に断つ。
  const valueRef = useRef(value)
  valueRef.current = value
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const onValidTextRef = useRef(onValidText)
  onValidTextRef.current = onValidText

  const prevFixedDayKeyRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const dayKey = fixedDate
      ? `${fixedDate.getFullYear()}-${fixedDate.getMonth()}-${fixedDate.getDate()}`
      : null
    const prevDayKey = prevFixedDayKeyRef.current
    prevFixedDayKeyRef.current = dayKey

    if (!fixedDate) return // fixedDate なし → 同期不要
    if (dayKey === prevDayKey) return // 日が変わっていない → 再同期しない（ループ防止）

    const currentValue = valueRef.current
    if (!currentValue) return
    if (isSameDay(currentValue, fixedDate)) return

    const next = applyFixedDatePart(currentValue, fixedDate)
    onValidTextRef.current?.()
    onChangeRef.current?.(next, dayjs(next).format('YYYY-MM-DD HH:mm'))
  }, [fixedDate])

  const isPointerDownCalenderCellRef = useRef(false)
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null
      if (target?.closest('.ant-picker-cell')) {
        isPointerDownCalenderCellRef.current = true
      }
    }
    const handlePointerUp = () => {
      isPointerDownCalenderCellRef.current = false
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('pointerup', handlePointerUp, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('pointerup', handlePointerUp, true)
    }
  }, [])

  // 固定モード時：固定日以外を選択不可にする。範囲指定時：範囲外の日を選択不可にする
  const rangeFrom = selectableDateRange?.from ?? null
  const rangeTo = selectableDateRange?.to ?? null
  const disabledDate = useMemo(() => {
    if (fixedDate) {
      return (current: unknown) => {
        const curDate = toDate(current)
        if (!curDate) return false
        return !isSameDay(curDate, fixedDate)
      }
    }
    if (!rangeFrom && !rangeTo) return undefined

    const fromDay = rangeFrom ? startOfDayTime(rangeFrom) : null
    const toDay = rangeTo ? startOfDayTime(rangeTo) : null
    return (current: unknown) => {
      const curDate = toDate(current)
      if (!curDate) return false
      const day = startOfDayTime(curDate)
      if (fromDay !== null && day < fromDay) return true
      if (toDay !== null && day > toDay) return true
      return false
    }
  }, [fixedDate, rangeFrom, rangeTo])

  // パネル操作（カレンダー選択）時のハンドラ
  const handleChange = useCallback(
    (date: unknown, dateString: string | string[]) => {
      const d = toDate(date)
      if (d) {
        const next = fixedDate ? applyFixedDatePart(d, fixedDate) : d
        onValidText?.()
        onChange?.(next, dayjs(next).format('YYYY-MM-DD HH:mm'))
      } else {
        onChange?.(null, typeof dateString === 'string' ? dateString : '')
      }
    },
    [fixedDate, onChange, onValidText]
  )

  // テキスト入力後の正規化（blur）時のハンドラ
  const handleBlur: React.FocusEventHandler<HTMLElement> = useCallback(
    (e) => {
      if (isPointerDownCalenderCellRef.current) {
        // 以下の問題があったため、カレンダーセルクリック時は blur 処理をスキップする
        // 1. 別の月の日付でマウスダウン
        //   ⇒ ここでテキストボックスからフォーカスがはずれ onBlur 発火
        // 2. onBlur 内の処理により内部で選択日が切り替わりカレンダーが選択月に切り替わる
        //   ⇒ このときマウスポインターが載っている日付が変わる
        // 3. そのままマウスアップで onChange が発火
        //   ⇒ マウスポインターが載っている別日で上書きされる
        return
      }
      const maybeInput = e.target as HTMLInputElement | null
      const inputEl =
        (maybeInput && 'value' in maybeInput ? maybeInput : null) ||
        (e.currentTarget as HTMLElement).querySelector('input')

      const raw = (inputEl?.value ?? '').trim()
      if (!raw) return

      const normalized = normalizeToYMDHM(raw)
      if (!normalized) {
        onInvalidText?.(VALIDATION_MESSAGES.DATETIME_INPUT_FORMAT)
        onChange?.(null, '')
        return
      }

      const next = fixedDate
        ? applyFixedDatePart(normalized.date, fixedDate)
        : normalized.date
      const text = dayjs(next).format('YYYY-MM-DD HH:mm')

      if (inputEl) inputEl.value = text
      onChange?.(next, text)
      onValidText?.()
    },
    [fixedDate, onChange, onInvalidText, onValidText]
  )

  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        // Enter submit 前に正規化を実行
        handleBlur(e as unknown as React.FocusEvent<HTMLElement>)
      }
    },
    [handleBlur]
  )

  const renderExtraFooter = useMemo(() => {
    if (!isFixed) return undefined
    return () => (
      <span className="text-xs text-gray-500">{DESCRIPTIONS.FIXED_DATE}</span>
    )
  }, [isFixed])

  return (
    <DatePicker
      {...rest}
      showTime={{ format: 'HH:mm', minuteStep: 5 }}
      format="YYYY-MM-DD HH:mm"
      value={value ?? null}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabledDate={disabledDate}
      renderExtraFooter={renderExtraFooter}
    />
  )
}
